import type { ClientStreamStatsMessage, PlaybackCapacityDecision, PlaybackQualitySnapshot, StreamHealthEvent } from '@playcastdotio/PeerCore';
import { ReportFreshnessGate } from '../../../../../../../../libs/PeerCore/src/integration/ReportFreshnessGate';
import { SustainedPoorPlaybackPolicy } from '../../../../../../../../libs/PeerCore/src/poorPlayback/SustainedPoorPlaybackPolicy';
import { VisiblePresentationRecovery } from '../../../../../../../../libs/PeerCore/src/presentationRecovery/VisiblePresentationRecovery';
import type { RecoveryHostCapacityStats, QualityTierDemotionHandler } from '../RecoveryCapacityGate';
import { TemporaryHandoffProtection } from '../networkHandoff/TemporaryHandoffProtection';
import { RecoveryTelemetry } from './RecoveryTelemetry';
import { ContinuousHealthEvidence } from './adaptationHardening/ContinuousHealthEvidence';

export interface MobileRecoveryDependencies {
  peerId: string;
  peerGeneration?: number;
  nowMs: () => number;
  currentTier: () => 'top' | 'middle' | 'bottom';
  configuredFps: () => number;
  currentCap: () => number;
  hardCap: () => number;
  bitrateApplied: () => boolean;
  protectBitrate: () => void;
  setHandoffCap: (cap: number | null) => void;
  resetPath: (epoch: number) => void;
  demote?: QualityTierDemotionHandler;
  resolutionFallbackUnavailableReason?: string;
  emit: (event: StreamHealthEvent) => void;
}

/** Joins one accepted guest interval with fresh host capacity, independently of encode FPS. */
export class MobileRecoveryBinding {
  private readonly freshness = new ReportFreshnessGate();
  private readonly policy: SustainedPoorPlaybackPolicy;
  private readonly presentation = new VisiblePresentationRecovery();
  private readonly handoff = new TemporaryHandoffProtection();
  private readonly telemetry: RecoveryTelemetry;
  private host?: RecoveryHostCapacityStats & { atMs: number };
  private epoch?: number;
  private episode = 0;
  private disposed = false;
  private lastDecision?: PlaybackCapacityDecision;
  private bottomFailureMs = 0;
  private bottomEpisode?: string;
  private exhausted = false;
  private lastAcceptedAt?: number;
  private qualifiedAt?: number;
  private decisionAt?: number;
  private freshAt?: number;
  private freshReportAt?: number;
  private modern = false;
  private metricEpisode?: string;
  private lastProducerAt?: number;
  private readonly continuity = new ContinuousHealthEvidence();
  private peakQualifyingMs = 0;

  constructor(private readonly deps: MobileRecoveryDependencies) {
    this.policy = new SustainedPoorPlaybackPolicy({ newEpisodeId: () => `${deps.peerId}:${deps.peerGeneration ?? 0}:playback:${++this.episode}` });
    this.telemetry = new RecoveryTelemetry(deps.emit, deps.nowMs);
  }

  public onHostCapacity(stats: RecoveryHostCapacityStats): void {
    this.host = { ...stats, atMs: this.deps.nowMs() };
  }

  public onClientStats(message: ClientStreamStatsMessage, receivedAtMs: number,
    producerTimeBounds?: { earliestMs: number; latestMs: number }): PlaybackCapacityDecision | undefined {
    const evidence = message.recoveryEvidence;
    if (this.disposed) return;
    if (!evidence && !this.modern) return;
    this.modern = true;
    this.freshReportAt = undefined;
    if (!evidence || message.seq === undefined) {
      this.policy.reset();
      this.presentation.reset();
      this.freshAt = undefined;
      this.lastAcceptedAt = undefined;
      this.resetMetrics();
      this.lastDecision = { state: 'unknown', qualifyingMs: 0, reason: 'missing-playback-evidence' };
      this.applyHandoff(message, false, receivedAtMs, false, this.lastDecision);
      return this.lastDecision;
    }
    const frameEvidence = evidence.presentation ?? { visibility: 'unknown' as const, playback: 'unknown' as const,
      progressSequence: 0, timingSource: 'unavailable' as const };
    const path = evidence.path;
    const epoch = path?.epoch ?? this.epoch ?? 0;
    if (this.epoch !== undefined && epoch < this.epoch) return { state: 'unknown', qualifyingMs: 0, reason: 'prior-path' };
    const changed = this.epoch !== undefined && epoch > this.epoch;
    if (changed) {
      this.presentation.reset();
      this.policy.reset();
      this.resetMetrics();
      this.lastProducerAt = undefined;
      this.deps.resetPath(epoch);
      this.lastAcceptedAt = undefined;
      this.bottomFailureMs = 0;
      this.exhausted = false;
      this.telemetry.record('path', 'network-path-changed', { epoch, reason: path!.reason });
    }
    this.epoch = epoch;
    if (!this.continuity.observe(receivedAtMs)) {
      this.policy.reset(); this.presentation.reset(); this.resetMetrics(); this.lastAcceptedAt = undefined;
    }
    const transitAge = message.sampledAtEpochMs === undefined ? undefined
      : ContinuousHealthEvidence.transitAge(receivedAtMs, producerTimeBounds);
    const accepted = this.freshness.accept({ epoch, sequence: message.seq, observationSequence: message.seq,
      observationAgeMs: 0, producerTimestampMs: message.sampledAtEpochMs, receivedAtMs, trustedTransitAgeMs: transitAge });
    // Regularly paced reports can still be seconds old. Only calibrated bounds grant authority.
    const producerAdvanced = message.sampledAtEpochMs !== undefined && Number.isFinite(message.sampledAtEpochMs)
      && (this.lastProducerAt === undefined || message.sampledAtEpochMs > this.lastProducerAt);
    if (producerAdvanced) this.lastProducerAt = message.sampledAtEpochMs;
    const freshness = transitAge === undefined ? { state: 'unavailable' as const }
      : !producerAdvanced ? { state: 'stale' as const } : accepted;
    if (freshness.state === 'fresh' && (freshness.ageMs ?? Infinity) < 3000)
      this.freshReportAt = receivedAtMs - freshness.ageMs!;
    const hostFresh = ContinuousHealthEvidence.fresh(receivedAtMs, this.host?.atMs);
    if (freshness.state !== 'fresh') this.presentation.reset();
    const presentation = freshness.state === 'fresh' ? this.presentation.observe({
      evidence: frameEvidence, peerConnected: false, nowMs: receivedAtMs,
    }) : undefined;
    const videoFlowing = (message.videoBitrateBps ?? 0) > 0
      || (hostFresh && this.host?.qualityLimitationReason === 'bandwidth');
    const intervalMs = Math.min(message.presentation?.elapsedMs ?? 1000,
      this.lastAcceptedAt === undefined ? 1000 : Math.max(0, receivedAtMs - this.lastAcceptedAt));
    this.lastAcceptedAt = receivedAtMs;
    const decision = this.policy.observe({ peerGeneration: this.deps.peerGeneration ?? 0, pathEpoch: epoch, sequence: message.seq,
      sampledAtMs: receivedAtMs, freshness, visibility: frameEvidence.visibility, playback: frameEvidence.playback,
      currentTier: this.deps.currentTier(), configuredFps: this.deps.configuredFps(), sourceActive: this.sourceActive(receivedAtMs),
      presentedFps: message.presentation?.presentedFps, freezeDelta: message.receiverCounters?.freezes ?? message.freezeDelta,
      freezeDurationDeltaMs: message.receiverCounters?.freezeMs, intervalMs,
      ongoingPresentationStallMs: videoFlowing && presentation?.state === 'stalled' ? presentation.stalledForMs : undefined,
      receivedVideoBitrateBps: message.videoBitrateBps, senderTargetBitrateBps: hostFresh ? this.host?.targetBitrateBps : undefined,
      lossPct: this.maximum(message.packetsLostPct, hostFresh ? this.host?.packetsLostPct : undefined),
      standingQueueMs: this.maximum(message.standingQueueMs, hostFresh ? this.host?.standingQueueMs ?? undefined : undefined),
      bandwidthLimited: hostFresh ? this.host?.qualityLimitationReason === 'bandwidth' : undefined,
      hostEvidenceFresh: hostFresh, bitrateProtectionApplied: this.deps.bitrateApplied() });
    this.freshAt = this.freshReportAt;
    if (decision.episodeId !== this.metricEpisode || decision.state === 'suspended' || decision.state === 'unknown') this.resetMetrics();
    this.metricEpisode = decision.episodeId;
    this.peakQualifyingMs = Math.max(this.peakQualifyingMs, decision.qualifyingMs);
    if (decision.qualifyingMs >= 6000 || decision.state === 'demote' || decision.state === 'at-bottom'
      || decision.reason === 'awaiting-bitrate') this.qualifiedAt ??= receivedAtMs;
    if (decision.requestTier) this.decisionAt = receivedAtMs;
    this.applyHandoff(message, changed, receivedAtMs, freshness.state === 'fresh', decision);
    this.lastDecision = decision;
    this.act(decision, intervalMs);
    return decision;
  }

  public blocksReclaim(): boolean {
    return this.modern && (this.lastDecision?.state !== 'healthy' || !ContinuousHealthEvidence.fresh(this.deps.nowMs(), this.freshAt));
  }

  public hasFreshTransportEvidence(): boolean {
    return ContinuousHealthEvidence.fresh(this.deps.nowMs(), this.freshReportAt);
  }

  public hasQualifiedCapacity(): boolean {
    return this.hasFreshTransportEvidence() && this.lastDecision !== undefined && this.lastDecision.qualifyingMs > 0
      && ['qualifying', 'demote', 'at-bottom'].includes(this.lastDecision.state);
  }

  public qualitySnapshot(): PlaybackQualitySnapshot & { resolutionFallbackUnavailableReason?: string } {
    return { peerGeneration: this.deps.peerGeneration ?? 0, pathEpoch: this.epoch ?? 0,
      ...(!this.deps.demote && this.deps.resolutionFallbackUnavailableReason
        ? { resolutionFallbackUnavailableReason: this.deps.resolutionFallbackUnavailableReason } : {}),
      ...(this.metricEpisode ? { episodeId: this.metricEpisode } : {}), qualifyingEvidenceMs: this.peakQualifyingMs,
      ...(this.decisionAt !== undefined && this.qualifiedAt !== undefined ? { decisionAfterQualificationMs: this.decisionAt - this.qualifiedAt } : {}),
      ...(this.freshAt !== undefined && this.deps.nowMs() >= this.freshAt ? { evidenceAgeMs: this.deps.nowMs() - this.freshAt } : {}) };
  }

  private resetMetrics(): void {
    this.metricEpisode = undefined; this.qualifiedAt = undefined; this.decisionAt = undefined; this.peakQualifyingMs = 0;
  }

  private sourceActive(nowMs: number): true | undefined {
    const source = this.host?.sourceCadence;
    if (!source || !ContinuousHealthEvidence.fresh(nowMs, source.sampledAtMs)
      || !Number.isFinite(source.intervalMs) || source.intervalMs <= 0 || source.intervalMs > 3000
      || !Number.isSafeInteger(source.framesDelta) || source.framesDelta < 0) return undefined;
    return source.framesDelta * 1000 / source.intervalMs >= Math.min(20, this.deps.configuredFps() * 0.5) ? true : undefined;
  }

  private maximum(left?: number, right?: number): number | undefined {
    const values = [left, right].filter((value): value is number => value !== undefined && Number.isFinite(value) && value >= 0);
    return values.length ? Math.max(...values) : undefined;
  }

  public dispose(): void { this.disposed = true; this.policy.reset(); this.presentation.reset(); this.handoff.reset(); }

  private applyHandoff(message: ClientStreamStatsMessage, changed: boolean, nowMs: number, fresh: boolean, decision: PlaybackCapacityDecision): void {
    const path = message.recoveryEvidence?.path ?? {
      epoch: this.epoch ?? 0, selection: 'unavailable' as const, reason: 'unavailable' as const,
    };
    const protection = this.handoff.observe({ path: { current: path, changed }, currentMaxBitrateBps: this.deps.currentCap(),
      hardCutMaxBitrateBps: this.deps.hardCap(), healthy: decision.state === 'healthy', evidenceFresh: fresh, nowMs });
    this.deps.setHandoffCap(protection.maxBitrateBps ?? null);
  }

  private act(decision: PlaybackCapacityDecision, intervalMs: number): void {
    if (decision.episodeId !== this.bottomEpisode) {
      this.bottomEpisode = decision.episodeId;
      this.bottomFailureMs = 0;
      this.exhausted = false;
    }
    this.telemetry.record('quality', 'quality-tier-decision', { ...decision });
    if (decision.state === 'qualifying' || decision.state === 'demote' || decision.state === 'at-bottom') this.deps.protectBitrate();
    if (decision.requestTier) {
      const effect = this.deps.demote;
      if (!effect) this.recordEffectFailure(decision, this.deps.resolutionFallbackUnavailableReason ?? 'missing-resolution-effect');
      else void effect(this.deps.peerId, 'bottom', 'sustained-poor-playback', {
        isCurrent: () => !this.disposed && this.metricEpisode === decision.episodeId,
      }).catch(() => {
        if (!this.disposed && this.metricEpisode === decision.episodeId) this.recordEffectFailure(decision, 'resolution-effect-failed');
      });
    }
    if (decision.state === 'at-bottom') {
      this.bottomFailureMs += intervalMs;
      if (this.bottomFailureMs >= 10000 && !this.exhausted) {
        this.exhausted = true;
        this.telemetry.record('exhausted', 'recovery-action', { episodeId: decision.episodeId!, pathEpoch: this.epoch ?? 0,
          owner: 'transport', action: 'protect-bitrate', outcome: 'exhausted', elapsedMs: this.bottomFailureMs, reason: 'capacity-insufficient-at-bottom' });
      }
    } else if (decision.state === 'healthy') { this.bottomFailureMs = 0; this.exhausted = false; }
  }

  private recordEffectFailure(decision: PlaybackCapacityDecision, reason: string): void {
    this.telemetry.record('effect', 'quality-tier-decision', {
      state: 'unknown', reason, qualifyingMs: decision.qualifyingMs,
      ...(decision.episodeId ? { episodeId: decision.episodeId } : {}),
    });
  }
}
